import React, { useMemo, createRef, useEffect } from "react";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux'
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import { withDragAndDrop } from "./dnd";
import { set, differenceInMinutes, isEqual } from 'date-fns'
import Popper from '@material-ui/core/Popper';
import Modal from '@material-ui/core/Modal';
import { AnimatePresence } from "framer-motion"

import { Animation } from 'components'
import { ItemInput, ItemModal, ItemOptions } from 'features'
import { DayModal } from './components'

import { Item } from 'schema'
import { momentDate, timezoneDate } from 'utils'
import { userContext, pageContext } from 'context'

import './react-big-calendar.css'

// import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
// import "react-big-calendar/lib/css/react-big-calendar.css";

const Container = styled.div`
    width: 100%;
    height: 100%;
    min-height: 50vh;
    flex-grow: 4;
    display: flex;
    /* margin-top: 0.5rem; */
`

// remember the browser's local timezone, as it might by used later on
BigCalendar.tz = moment.tz.guess();
// format all dates in BigCalendar as they would be rendered in browser's local timezone (even if later on they won't)
const m = (...args) => moment.tz(...args, BigCalendar.tz);
m.localeData = moment.localeData;

const localizer = momentLocalizer(m);
const DnDCalendar = withDragAndDrop(BigCalendar);

const Calendar = props => {
    const {
        events,
        view,
        date,
        timeslots,
        onSchedule,
        onDrillDown,
        components,
        styles,
        collectRefs,
        getNow,
        scrollToTime,
        animate,
        step,
        min,
        max,
        initialHour,
        disable,
        disableDND,
        onDisable,
        onSelectEvent
    } = props;
    const user = useSelector((state) => state.auth.user)
    let timezone = user ? user['timezone'] : moment.tz.guess()
    // moment.tz.setDefault(timezone)
    const refs= useMemo(
        () => Array.from({ length: 25 }).map(() => createRef()),[]
    );
    
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const [start, setStart] = React.useState(null)
    const [end, setEnd] = React.useState(null)
    const [selected, setSelected] = React.useState(null)
    const [slot, setSlot] = React.useState(null)

    const [anchorEl, setAnchor] = React.useState(null)
    const [popOut, setPopOut] = React.useState(null)
    const [lastPageID, setPage] = React.useState(() => {
        if(user){
            if(user['activePageType'] === pageContext.TYPES.PAGES){
                return user['activePageID']
            } else {
                return user['activePages'][0]
            }
        } else {
            return null
        }
    })
    const [itemSelected, setItem] = React.useState(false)
    const [mouseDown, setMousedown] = React.useState(false)

    useEffect(() => {
        collectRefs && collectRefs(refs)
        // console.log('[Calendar]', refs)
        // refs[initialHour-1] && refs[initialHour-1].current.scrollIntoView()        
    }, [])

    React.useEffect(() => {
        popOut && window.addEventListener('wheel', preventScroll, { passive: false })
        popOut && window.addEventListener('keydown', onKeyDown)
        return () => {
            window.removeEventListener('wheel', preventScroll)
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [popOut])

    const preventScroll = (e) => {
        const scrollableElements = e.path.filter(x => x.scrollHeight !== x.clientHeight)
        const scrollableTagNames = scrollableElements.map(x => x.tagName)
        // console.log(e.path[1].id)
        if(scrollableTagNames[0] === 'HTML'){
            e.preventDefault();
            e.stopPropagation();
            return false;
        } else {
            return true
        }
    }

    const onKeyDown = (e) => {
        if([38,40].includes(e.keyCode)){
            e.preventDefault();
            return false
        } else {
            return true
        }
    }

    /////////////////////////////
    // Event schedule handlers //
    /////////////////////////////
    const onEventResize = (data) => {
        const start_time = data['start']
        const end_time = data['end']
        const start_date  = set(start_time, {hours: 0, minutes: 0, seconds: 0})
        const end_date = set(end_time, {hours: 0, minutes: 0, seconds: 0})
        const item = data['event']['item']
        const schedule = {
            start_date: timezoneDate(start_date, timezone),
            start_time: timezoneDate(start_time, timezone),
            end_date: timezoneDate(end_date, timezone),
            end_time: timezoneDate(end_time, timezone),
        }
        // console.log('[onEventResize]', schedule['start_time'].toDate(), schedule['start_time'].format('D-M-Y h:mma'))

        onSchedule && onSchedule({id: data['resourceId'], item, schedule})
    };
    
    const onEventDrop = (data) => {
        // console.log('[onEventDrop]', data);
        if(!disable){
            // onSchedule && onSchedule(data)
            const start_time = data['start']
            const end_time = data['end']
            const start_date  = set(start_time, {hours: 0, minutes: 0, seconds: 0})
            const end_date = set(end_time, {hours: 0, minutes: 0, seconds: 0})
            const item = data['event']['item']
            const schedule = {
                start_date: timezoneDate(start_date, timezone),
                start_time: timezoneDate(start_time, timezone),
                end_date: timezoneDate(end_date, timezone),
                end_time: timezoneDate(end_time, timezone),
            }
            onSchedule && onSchedule({id: data['resourceId'], item, schedule})
        } else {
            onDisable && onDisable()
        }
    };

    const onExtEventDrop = (id, item, schedule) => {
        if(!disable){
            onSchedule && onSchedule({id: id, item: item, schedule})
        } else {
            onDisable && onDisable()
        }
    }

    ////////////////////
    // Slot handlers //
    //////////////////
    const onSlotDown = (schedule, event) => {
        // console.log('[Calendar.js]', schedule, event)
        if(!disable){
            setSelected(schedule)
            setMousedown(true)
        } else {
            onDisable && onDisable()
        }
    }

    const onSlotHover = (schedule, event, slot) => {
        // console.log('[Calendar.js]', schedule, event)
        setSelected(schedule)
        setSlot(slot)
    }

    const onSlotUp = (position) => {
        // console.log('[Calendar.js] onSlotUp', selected)
        window.event.preventDefault()
        const event = window.event
        const anchor = {
            getBoundingClientRect: () => ({
                top: event.clientY,
                left: event.clientX,
                bottom: event.clientY,
                right: event.clientX,
                width: 1,
                height: 1,
            }),
            clientWidth: 1,
            clientHeight: 1
        }

        selected && setAnchor(anchorEl ? null : anchor);
        const inputElement = renderInput(anchor, selected, position)
        selected && setPopOut(inputElement)
        setMousedown(false)
        // alert(selected['start_time'].format('D-M-Y h:mma'), selected['start_time'].toDate())
        // setSelected(null)
    }

    const resetSelection = () => {
        setSelected(null)
        setStart(null)
        setEnd(null)
        setAnchor(null)
    }

    const onDrillDownHandler = (date, extra, extra2) => {
        onDrillDown && onDrillDown(date)
    }

    ////////////////////
    // Event handlers //
    ////////////////////
    // const onEventSelect = (event, data) => {
    //     // console.log('[Calendar] onEventSelect', {event: event, data: data})
    //     // Setting anchorEl
    //     const anchor = {
    //         getBoundingClientRect: () => ({
    //             top: event.clientY,
    //             left: event.clientX,
    //             bottom: event.clientY,
    //             right: event.clientX,
    //             width: 1,
    //             height: 1,
    //         }),
    //         clientWidth: 1,
    //         clientHeight: 1
    //     }
    //     setAnchor(anchorEl ? null : anchor);

    //     // Setting ItemModal
    //     setPopOut(<Animation animation='default'><ItemModal id='itemmOADAL' itemID={data['item']['itemID']} onClose={resetPopper} onEsc/></Animation>)

    // }

    const onEventSelectHandler = (event, data) => {
        const item = data['item']
        if(onSelectEvent){
            onSelectEvent(data)
        } else {
            const anchor = {
                getBoundingClientRect: () => ({
                    top: event.clientY,
                    left: event.clientX,
                    bottom: event.clientY,
                    right: event.clientX,
                    width: 1,
                    height: 1,
                }),
                clientWidth: 1,
                clientHeight: 1
            }
            setAnchor(anchorEl ? null : anchor);
    
            // Setting ItemModal
            setPopOut(<Animation animation='default'><ItemModal id='itemmOADAL' itemID={data['item']['itemID']} onClose={resetPopper} onEsc/></Animation>)
        }
    }

    const onEventContext = (event, data) => {
        console.log('[Calendar] onContextMenu', event)
        event.preventDefault();
        event.persist();
        const anchor = {
            getBoundingClientRect: () => ({
                top: event.clientY,
                left: event.clientX,
                bottom: event.clientY,
                right: event.clientX,
                width: 1,
                height: 1,
            }),
            clientWidth: 1,
            clientHeight: 1
        }
        setAnchor(anchorEl ? null : anchor);

        // Setting ItemOptions
        setPopOut(
            <Modal disableScrollLock onBackdropClick={resetPopper} onEscapeKeyDown={resetPopper} open={true} BackdropProps={{invisible:true}} id='MODAL'>
                <Popper style={{zIndex: 1300, outline:'none'}} id='PopOut' open={true} anchorEl={anchor} placement={'bottom-start'}>
                    <Animation animation={'default'}>
                        <ItemOptions item={data['item']} type='calendar' onClose={resetPopper} closeHandler={resetPopper}/>
                    </Animation>
                </Popper>
            </Modal>
        )
    }
    
    const resetPopper = () => {
        setPopOut(null)
        setAnchor(null)
        resetSelection()
        console.log('resetPopper')
    }

    ////////////
    // Render //
    ////////////
    const renderInput = (anchor, schedule, position) => {
        const sectionID = data['pages'][lastPageID]['sections'][0]
        const defaultItem = new Item({
            pageID: lastPageID,
            sectionID: sectionID,
            start_date: schedule && schedule['start_date'],
            start_time: schedule && schedule['start_time'],
            end_date: schedule && schedule['end_date'],
            end_time: schedule && schedule['end_time'],
        })
        const margin = Math.abs(differenceInMinutes(defaultItem['start_time'], defaultItem['end_time'])) <= 60 ? '1rem 0' : '0.5rem 0'
        return <Modal onBackdropClick={resetPopper} onEscapeKeyDown={resetPopper} open={anchor ? true : false} BackdropProps={{invisible: true}}>
                    <Popper style={{zIndex: 1300, outline:'none', margin: margin}} id='PopOut' open={anchor ? true : false} anchorEl={anchor} placement={position || 'bottom-start'}>
                        <Animation animation='default'>
                            <ItemInput sectionSelector onSelectPage={(pageID) => setPage(pageID)} onClose={resetPopper}
                                element='ITEM_INPUT' type='Input' disableAnimation
                                pageID={lastPageID} sectionID={sectionID} schedule={schedule}
                                item={defaultItem} parents={[]} />
                        </Animation>
                    </Popper>
                </Modal>
    }

    const onShowMore = (events, date) => {
        window.event.preventDefault()
        const event = window.event
        const anchor = {
            getBoundingClientRect: () => ({
                top: event.clientY,
                left: event.clientX,
                bottom: event.clientY,
                right: event.clientX,
                width: 1,
                height: 1,
            }),
            clientWidth: 1,
            clientHeight: 1
        }

        const popOut =  <Modal onBackdropClick={resetPopper} onEscapeKeyDown={resetPopper} open={anchor ? true : false} BackdropProps={{invisible: true}}>
                            <Popper style={{zIndex: 1300, outline:'none', marginBottom: '0.5rem'}} id='PopOut' open={anchor ? true : false} anchorEl={anchor} placement={'top'}>
                                <Animation animation='default'>
                                    <DayModal events={events} date={date} styles={styles} setPopOut={setPopOut}/>
                                </Animation>
                            </Popper>
                        </Modal>
        setPopOut(popOut)
    }

    //////////////////
    // Date formats //
    //////////////////
    const formats = {
        timeGutterFormat: (date, culture, localizer) => (localizer.format(date, 'ha')),
        eventTimeRangeFormat: ({start, end}, culture, localizer) => {
            const a = localizer.format(start, 'h:mm')
            const b = localizer.format(end, 'h:mma')
            if(!isEqual(start, end)){
                return `${a} - ${b}`
            } else {
                return b
            }
        },
    }

    return (
        <Container>
            <DnDCalendar
                defaultDate={moment().tz(timezone)}
                view={view}
                date={date}
                views={['day','week','work_week','month']}
                defaultView="month"
                events={events}
                localizer={localizer}
                formats={formats}
                timezone={timezone}
                disableDND={disableDND}
                getNow={getNow}
                min={min}
                max={max}
                scrollToTime={scrollToTime}
                // scrollToTime={new Date()}
                animate={animate}
                resizable
                toolbar={false}

                // Control size of each slot
                timeslots={step ? 60/step : 6}
                step={step || 10}
                
                scheduleDuration={{minute: 60}}
                // scheduleDuration={{minute: step || 60}}
                drilldownView='day'
                // selectable
                style={{ height: "auto", width: '100%', flexGrow: 4, overflow: 'hidden' }}
                styles={styles}

                selected={selected}
                slot={slot}
                mouseDown={mouseDown}

                onDrillDown={onDrillDownHandler}
                onEventDrop={onEventDrop}
                onEventResize={onEventResize}
                onExtEventDrop={onExtEventDrop}
                // onSelectSlot={view !== 'month' && onSelectSlot}

                onSlotDown={!itemSelected && onSlotDown}
                onSlotHover={!itemSelected && onSlotHover}
                onSlotUp={!itemSelected && onSlotUp}

                onEventSelect={onEventSelectHandler}
                onEventContext={onEventContext}
                onEventOver={(e) => setItem(e)}

                onShowMore={onShowMore}
                refs={refs}
            />

            <AnimatePresence>
                {popOut}
            </AnimatePresence>
        </Container>
    )
}

export default Calendar

Calendar.propTypes = {
    components: PropTypes.shape(
        {
            eventElement: PropTypes.elementType,
            eventContext: PropTypes.elementType,
            slotInput: PropTypes.elementType
        }
    ),
}